import type { DebouncedFunc } from 'lodash';
import { debounce as createDebounced } from 'lodash';
import { useEffect, useRef } from 'react';

type Callback = (...args: any) => void | undefined;
export type CancelableCallback = DebouncedFunc<Callback> | null;
export type Debounced = React.MutableRefObject<CancelableCallback>;

function noop(): void {}

/**
 * This takes a function and a duration and debounces the value.
 * It will take care of cancelling any timeout when the component
 * is removed and ensures the callback always has access to latest state/props.
 * @param fn
 * @param duration
 */
export function useDebouncedCallback(fn?: Callback, duration?: number): CancelableCallback | Callback {
  const callbackFn = fn || noop;

  // Keep the callback in a ref to always have access to latest version
  const fnRef = useRef(callbackFn);

  // Update the ref whenever the callback changes
  useEffect(() => {
    fnRef.current = callbackFn;
  }, [callbackFn]);

  // Create the debounced function once and store it in a ref
  const debouncedRef = useRef<CancelableCallback | Callback>(null);

  // Create/recreate the debounced function when duration changes
  useEffect(() => {
    if (duration) {
      // Create a debounced function that always calls the latest callback via fnRef
      debouncedRef.current = createDebounced((...args) => fnRef.current(...args), duration);
    } else {
      // No debouncing, just call the function directly
      debouncedRef.current = (...args: any[]) => fnRef.current(...args);
    }

    // Cleanup function to cancel any pending debounced calls
    return () => {
      if (debouncedRef.current && 'cancel' in debouncedRef.current) {
        debouncedRef.current.cancel();
      }
    };
  }, [duration]);

  // Return the debounced function (or a fallback if not yet initialized)
  return debouncedRef.current || ((...args: any[]) => fnRef.current(...args));
}
